import React from 'react';
import {

    Box,
    Typography,
    IconButton,
    Dialog,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { Close, Visibility, VisibilityOff } from '@mui/icons-material';
import { glassStyles } from '../../tokens/designTokens';
import InitialsAvatar from '../widgets/InitialsAvatar';
import BottomSheet, { BottomSheetDraggableHeader } from './BottomSheet'; // Adjust path if needed, likely ../widgets/InitialsAvatar
import { CollaboratorProfile } from '../../types/watchlist'; // Adjust path
import { useUserProfileCache } from '../../contexts/UserProfileCacheContext';
import { font } from '../../utils/fontHelper';
import { parseFirebaseDate, formatFirebaseDate, formatWatchedDate } from '../../utils/dateUtils';

interface CollaboratorsWatchedDialogProps {
    open: boolean;
    onClose: () => void;
    movieTitle: string;
    collaborators: CollaboratorProfile[];
    watchedBy: string[];
    watchedDates?: Record<string, string | Date | any>; // Map of uid -> date
}

const CollaboratorsWatchedDialog: React.FC<CollaboratorsWatchedDialogProps> = ({
    open,
    onClose,
    movieTitle,
    collaborators,
    watchedBy,
    watchedDates = {},
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isDark = theme.palette.mode === 'dark';
    const { getUserDisplayName, getUserAvatar } = useUserProfileCache();

    const renderContent = () => (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            p: 0,
        }}>
            {/* Header */}
            <BottomSheetDraggableHeader sx={{
                p: 3,
                pb: 2,
                borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0,
            }}>
                <Box>
                    <Typography
                        variant="h6"
                        sx={{
                            fontFamily: font(),
                            fontWeight: 600,
                            fontSize: 18,
                            color: theme.palette.text.primary,
                            mb: 0.5,
                        }}
                    >
                        Watched by
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            fontFamily: font(),
                            color: theme.palette.text.secondary,
                            fontSize: 13,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 300,
                        }}
                    >
                        {movieTitle}
                    </Typography>
                </Box>
                <IconButton
                    onClick={onClose}
                    sx={{
                        p: 1,
                        color: theme.palette.text.secondary,
                        bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                        '&:hover': {
                            bgcolor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                        },
                    }}
                >
                    <Close />
                </IconButton>
            </BottomSheetDraggableHeader>

            {/* List */}
            <Box sx={{
                flex: 1,
                overflowY: 'auto',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
            }}>
                {collaborators.length === 0 ? (
                    <Typography sx={{ p: 2, textAlign: 'center', color: theme.palette.text.secondary }}>
                        No collaborators found.
                    </Typography>
                ) : (
                    collaborators.map((collaborator) => {
                        const isWatched = watchedBy.includes(collaborator.uid);
                        const displayName = getUserDisplayName(collaborator.uid, collaborator.name);
                        const avatar = getUserAvatar(collaborator.uid) || collaborator.avatar;

                        return (
                            <Box
                                key={collaborator.uid}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 1.5,
                                    borderRadius: 3,
                                    bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                                    }
                                }}
                            >
                                {/* User Info */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <InitialsAvatar
                                        src={avatar}
                                        name={displayName}
                                        size={40}
                                    />
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography
                                            sx={{
                                                fontFamily: font(),
                                                fontWeight: 500,
                                                fontSize: 14,
                                                color: theme.palette.text.primary,
                                            }}
                                        >
                                            {displayName}
                                        </Typography>
                                        {isWatched && watchedDates[collaborator.uid] && (
                                            <Typography
                                                sx={{
                                                    fontFamily: font(),
                                                    fontSize: 11,
                                                    color: theme.palette.text.secondary,
                                                }}
                                            >
                                                Watched {formatWatchedDate(watchedDates[collaborator.uid], undefined, '')}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>

                                {/* Status Icon */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        bgcolor: isWatched
                                            ? (isDark ? 'rgba(48, 219, 91, 0.15)' : 'rgba(48, 219, 91, 0.1)')
                                            : 'transparent',
                                    }}
                                >
                                    {isWatched ? (
                                        <Visibility
                                            sx={{
                                                fontSize: 20,
                                                color: isDark ? '#30DB5B' : '#248A3D',
                                            }}
                                        />
                                    ) : (
                                        <VisibilityOff
                                            sx={{
                                                fontSize: 20,
                                                color: theme.palette.text.disabled,
                                            }}
                                        />
                                    )}
                                </Box>
                            </Box>
                        );
                    })
                )}
            </Box>
        </Box>
    );

    if (isMobile) {
        return (
            <BottomSheet
                open={open}
                onClose={onClose}
                maxHeight="80vh"
                modalProps={{
                    disableAutoFocus: true,
                    disableEnforceFocus: true,
                    disableRestoreFocus: true,
                }}
            >
                {renderContent()}
            </BottomSheet>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    overflow: 'hidden',
                    maxHeight: '600px',
                    ...glassStyles.popup(isDark),
                }
            }}
        >
            <IconButton
                onClick={onClose}
                size="small"
                sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    color: theme.palette.text.secondary,
                    zIndex: 1,
                }}
            >
                <Close />
            </IconButton>
            {renderContent()}
        </Dialog>
    );
};

export default CollaboratorsWatchedDialog;
